/**
 * Firebase Storage Proxy
 * 
 * This module provides direct access to Firebase Storage with proper CORS configuration.
 * It ensures files are properly uploaded and retrieved from Firebase Storage in both
 * development and production environments.
 */

import { storage } from './firebase';
import { ref, uploadBytes, getDownloadURL, UploadMetadata } from 'firebase/storage';

// Determine if we're in a development environment
const isDevelopment = 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1';

console.log(`[STORAGE] Running in ${isDevelopment ? 'development' : 'production'} mode`);

/**
 * Upload a blob to Firebase Storage
 * 
 * @param blob - The blob to upload
 * @param path - The storage path
 * @param metadata - Optional metadata for the upload
 * @returns Promise with the download URL
 */
export const uploadToStorage = async (
  blob: Blob,
  path: string,
  metadata?: UploadMetadata
): Promise<string> => {
  if (!storage) {
    throw new Error('Firebase storage not initialized');
  }

  console.log(`[STORAGE] Uploading to ${path}, blob size: ${blob.size}, type: ${blob.type}`);
  
  try {
    // Create a reference to the storage location
    const storageRef = ref(storage, path);
    
    // Set content type in metadata if not already set
    const updatedMetadata = {
      ...metadata,
      contentType: blob.type || 'audio/mpeg'
    };
    
    // Upload the blob with metadata
    const uploadResult = await uploadBytes(storageRef, blob, updatedMetadata);
    console.log('[STORAGE] Upload complete:', uploadResult);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    console.log('[STORAGE] Got download URL:', downloadURL);
    
    return downloadURL;
  } catch (error) {
    console.error('[STORAGE] Upload failed:', error);
    throw error;
  }
};

/**
 * Get a download URL for a file in Firebase Storage
 */
export const getStorageDownloadURL = async (path: string): Promise<string> => {
  if (!storage) {
    throw new Error('Firebase storage not initialized');
  }

  try {
    // Only use the new path structure
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error(`[STORAGE] Error getting download URL for ${path}:`, error);
    throw error;
  }
};
